import {useEffect, useMemo} from 'react';
import {OvbuilderDetails_ovrecommendDetails} from '../../../schema-types/OvbuilderDetails';
import {SelectOption} from '../../../components/AutoForm/AutoFieldNew';
import {
  SetFieldValueWithSourceKey,
  AutoFieldHelpers,
  useAutoField,
} from '../../../components/AutoForm/useAutoForm';
import {mainPlanTypeType, builderFieldsType} from '../CreateBuilder/builderFields';
import type {
  newPlanFields,
  newPlanParentFields,
  newSubplanFields,
} from '../BuilderSetUpNew/builderSetUpNewFields';
import type {modelPortfolioField} from '../BuilderModify/builderModifyFieldTypes';
import {
  defaultFeeFields,
  getPlanFeesFormikFromServer,
} from '../CreateBuilder/BuilderPlanFees';

import {includesSaltus} from '../../../utils/includesSaltus';
import {getEnvironment} from '../../../utils/envTest';
import usePlanNamesProviders, {
  PlanProviderSelectOption,
} from '../../../hooks/usePlanNamesProviders';
import {UPDATES} from '../../../shared/UPDATES';

// ============================================
// NewPlanHelpers.tsx (503, 505-518)
// ============================================

export const getFilteredProductNames = ({
  productNames,
  minor,
  mainPlanType,
}: {
  productNames: SelectOption<string>[] | undefined;
  minor?: boolean;
  mainPlanType: mainPlanTypeType;
}): SelectOption<string>[] => {
  if (!productNames) {
    return [];
  }
  const filtered = [
    ...productNames.filter((x) => (minor ? x.label !== 'ISA' : x.label !== 'J-ISA')),
  ]
    .filter((a) => {
      if (!minor && mainPlanType === 'isa') {
        return !a.label.toLocaleLowerCase().includes('junior');
      }
      if (minor && mainPlanType === 'isa') {
        return a.label.toLocaleLowerCase().includes('junior');
      }
      return true;
    })
    .sort((a, b) => {
      if (a.label.toLocaleLowerCase().includes('self')) {
        return -1;
      }
      if (b.label.toLocaleLowerCase().includes('self')) {
        return 1;
      }
      if (a.label.toLocaleLowerCase().includes('junior')) {
        return 1;
      }
      if (b.label.toLocaleLowerCase().includes('junior')) {
        return -1;
      }
      return 0;
    });

  const envName = getEnvironment();
  if (envName === 'PROD') {
    // ON-3342 - Remove LISA temporarily
    filtered.filter((x) => !x.label.toLocaleLowerCase().includes('lifetime'));
  }
  return filtered;
};

export const useDefaultProduct = (displayProductNames: SelectOption<string>[]) => {
  const [productNameIdField, , productNameIdHelpers] = useAutoField<string | undefined>(
    'setUpNew.parent.productNameId'
  );
  useEffect(() => {
    if (!displayProductNames.length) {
      return;
    }
    if (!displayProductNames.find((x) => x.value === productNameIdField.value)) {
      productNameIdHelpers.setValue(UPDATES._D503, undefined, true);
    }
  }, [displayProductNames, productNameIdField.value]);
};

export const getNewPlanProviders = (
  providers: PlanProviderSelectOption[] | undefined
): PlanProviderSelectOption[] =>
  providers?.filter((x) => !x.label.toLocaleLowerCase().includes('quilter')) ?? [];

export const useShowMandateSelector = () => {
  const [parentPlanProviderField] = useAutoField<newPlanParentFields['planProvider']>(
    'setUpNew.parent.planProvider'
  );
  const {productProviders} = usePlanNamesProviders();
  return useMemo(() => {
    if (!parentPlanProviderField.value) return false;
    const providerOption = productProviders?.find(
      (p) => p.value === parentPlanProviderField.value
    );
    return providerOption?.showMandateSelector ?? false;
  }, [parentPlanProviderField.value, productProviders]);
};

// ============================================
// BuilderNewPlan.tsx (500-501)
// ============================================

const getIsLowes = (
  selectedPlanProvider?: string,
  productProviders?: PlanProviderSelectOption[],
  mainPlanType?: mainPlanTypeType
) => {
  if (!selectedPlanProvider || !productProviders?.length || mainPlanType !== 'pension') {
    return false;
  }
  return Boolean(
    productProviders.find(
      (pp) =>
        pp.value === selectedPlanProvider &&
        pp.label.toLocaleLowerCase().includes('lowes')
    )
  );
};

export const getIsTransactBond = (
  selectedPlanProvider?: string,
  selectedPlanType?: string,
  productProviders?: PlanProviderSelectOption[],
  productNames?: SelectOption[],
  mainPlanType?: mainPlanTypeType
) => {
  if (
    !selectedPlanProvider ||
    !selectedPlanType ||
    !productProviders?.length ||
    !productNames?.length ||
    mainPlanType !== 'bond'
  ) {
    return false;
  }

  // Check if provider is Transact
  const isTransactProvider = Boolean(
    productProviders.find(
      (pp) =>
        pp.value === selectedPlanProvider &&
        pp.label.toLocaleLowerCase().includes('transact')
    )
  );

  // Check if product type is Investment Bond
  const isInvestmentBond = Boolean(
    productNames.find(
      (product) =>
        product.value === selectedPlanType &&
        product.label.toLocaleLowerCase().includes('investment bond')
    )
  );

  return isTransactProvider && isInvestmentBond;
};

export const useDeriveIsLowesPension = (mainPlanType: mainPlanTypeType | undefined) => {
  const [planProviderObj] = useAutoField<newPlanParentFields['planProvider']>(
    'setUpNew.parent.planProvider'
  );
  const planNameProviderUtils = usePlanNamesProviders();
  const isLowesPension = useMemo(() => {
    return getIsLowes(
      planProviderObj.value,
      planNameProviderUtils.productProviders,
      mainPlanType
    );
  }, [planProviderObj.value, planNameProviderUtils.productProviders, mainPlanType]);
  return isLowesPension;
};

export const useDeriveIsTransactBond = (mainPlanType: mainPlanTypeType | undefined) => {
  const [planProviderObj] = useAutoField<newPlanParentFields['planProvider']>(
    'setUpNew.parent.planProvider'
  );
  const [planTypeObj] = useAutoField<newPlanParentFields['productNameId']>(
    'setUpNew.parent.productNameId'
  );
  const planNameProviderUtils = usePlanNamesProviders();
  const isTransactBond = useMemo(() => {
    return getIsTransactBond(
      planProviderObj.value,
      planTypeObj.value,
      planNameProviderUtils.productProviders,
      planNameProviderUtils.productNames,
      mainPlanType
    );
  }, [
    planProviderObj.value,
    planTypeObj.value,
    planNameProviderUtils.productProviders,
    planNameProviderUtils.productNames,
    mainPlanType,
  ]);
  return isTransactBond;
};

// ============================================
// BuilderNewPlanForm.tsx (502, 504, 525, 526)
// ============================================

export const updateIsSaltusProvider = ({
  isSaltusProviderHelpers,
  productProviders,
  planProvider,
}: {
  isSaltusProviderHelpers: AutoFieldHelpers<boolean>;
  productProviders: ReturnType<typeof usePlanNamesProviders>['productProviders'];
  planProvider: string | undefined;
}) => {
  isSaltusProviderHelpers.setValue(
    UPDATES._D525,
    includesSaltus(
      productProviders?.find((x) => x.value === planProvider)?.label as string
    ),
    true
  );
};

export const deriveIsNewLisa = ({
  lisaId,
  planTypeFieldValue,
  setFieldValue,
}: {
  lisaId: string | undefined;
  planTypeFieldValue: string | undefined;
  setFieldValue: SetFieldValueWithSourceKey<builderFieldsType>;
}) => {
  const isLisa = !!lisaId && planTypeFieldValue === lisaId;
  setFieldValue(UPDATES._D502, 'isLisa', isLisa);
  return isLisa;
};

export const getSaltusSippPlanType = (
  displayProductNames: SelectOption<string>[] | undefined
): SelectOption<string> | undefined =>
  displayProductNames?.find(
    (x) => x.label.toLocaleLowerCase() === 'self invested personal pension'
  );

export const setDefaultSaltusSippPlanType = ({
  displayProductNames,
  isSaltusSIPP,
  productNameIdHelpers,
}: {
  displayProductNames: SelectOption<string>[] | undefined;
  isSaltusSIPP: boolean;
  productNameIdHelpers: AutoFieldHelpers<string | undefined>;
}) => {
  if (!displayProductNames?.length) {
    return;
  }
  const defaultSaltusSippPlanType = getSaltusSippPlanType(displayProductNames);
  if (defaultSaltusSippPlanType && isSaltusSIPP) {
    productNameIdHelpers.setValue(UPDATES._D504, defaultSaltusSippPlanType.value);
  }
};

export const clearMandateOnProviderBlur = ({
  mandateHelpers,
}: {
  mandateHelpers: AutoFieldHelpers<modelPortfolioField | undefined>;
}) => {
  mandateHelpers.setValue(UPDATES._D526, undefined, true);
};

// ============================================
// BuilderSetUpNew.tsx (524)
// ============================================

// ============================================
// SET UP NEW — initialValueOverrides approach (replaces loadSavedSetUpBuilderNewPlan)
// ============================================

const defaultSubplanNew: newSubplanFields = {
  existingPotId: null,
  planFees: defaultFeeFields,
};

export const getSetUpNewInitialValues = (
  builderDetails: OvbuilderDetails_ovrecommendDetails | null
): newPlanFields | undefined => {
  if (!builderDetails?.setUpNew) return undefined;

  // Fee data — including the SPP ongoing FP fee percentage — lives on the
  // OVRecommendPot entries in `potPlans`, not on `setUpNew.subplans` (which
  // are plain PotType and carry no fees). Match the two by pot id so saved
  // fees rehydrate the form instead of always falling back to defaults.
  const ovPotByPotId = new Map(
    (builderDetails.potPlans ?? []).map((ovPot) => [ovPot.pot.id, ovPot])
  );
  const subplanPotIds = new Set(builderDetails.setUpNew.subplans?.map((s) => s?.id));
  const wrapperOvPot = builderDetails.potPlans?.find(
    (ovPot) => !subplanPotIds.has(ovPot.pot.id)
  );
  const wrapperFpFee = wrapperOvPot?.fees?.find((fee) =>
    fee.name?.includes('Off Platform: FP Fee')
  );

  return {
    parent: {
      planProvider: builderDetails.setUpNew.productProvider?.id ?? '',
      productNameId: builderDetails.setUpNew.productType?.id ?? '',
      planFees: {...defaultFeeFields, fpFeeId: wrapperFpFee?.id ?? null},
      contactId: '', // TODO: add contact to setUpNew fragment + backend
    },
    subplans: builderDetails.setUpNew.subplans?.map((s): newSubplanFields => {
      const ovPot = s ? ovPotByPotId.get(s.id) : undefined;
      return {
        existingPotId: s!.id,
        planFees: getPlanFeesFormikFromServer(ovPot) ?? defaultFeeFields,
        model: (s?.modelPortfolio || s?.mandate) ?? undefined,
      };
    }) ?? [defaultSubplanNew],
  };
};
